import dayjs, { type Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/**
 * Format the date to compact format based on recency.
 *
 * - If same day: return "HH:mm" format
 * - If same week: return weekday name (e.g., "Thứ Hai")
 * - If same month: return "DD/MM" format
 * - If same year: return "DD/MM" format
 * - Otherwise: return "DD/MM/YYYY" format
 */
export const formatDateCompact = (date: Dayjs | Date | string | number): string => {
  const target = dayjs(date);
  const now = dayjs();

  // Same day - return time only
  if (target.isSame(now, 'day')) {
    return target.format('HH:mm');
  }

  // Same week - return weekday name
  const startOfWeek = now.startOf('week');
  const endOfWeek = now.endOf('week');
  if (target.isSameOrAfter(startOfWeek) && target.isSameOrBefore(endOfWeek)) {
    return target.format('dddd'); // Vietnamese weekday names when locale is set to 'vi'
  }

  // Same month - return DD/MM
  if (target.isSame(now, 'month')) {
    return target.format('DD/MM');
  }

  // Same year - return DD/MM
  if (target.isSame(now, 'year')) {
    return target.format('DD/MM');
  }

  // Different year - return DD/MM/YYYY
  return target.format('DD/MM/YYYY');
};

/**
 * Format the date to full human-readable format.
 * Used for hover tooltips and accessibility.
 *
 * @example "December 15, 2025 at 3:45 PM"
 */
export const formatDateFull = (date: Dayjs | Date | string | number): string => {
  return dayjs(date).format('MMMM D, YYYY [at] h:mm A');
};
